"use client";

import { localizeCopy } from "@/lib/localize-copy";

import { usePathname } from "next/navigation";
import { ChevronDown } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { useSiteLocale, useBlogLocaleNavigation } from "@/components/layout/CcLocaleContext";
import localeRegions from "@/data/locale-regions.json";
import { persistLanguageChoice, languageFlag } from "@/lib/i18n/language-suggestion";
import languageNames from "@/data/blog/languages.json";
import blogCopy from "@/data/copy/blog-page.json";
import { getLanguageSwitchOptions } from "@/lib/i18n/language-switching";
import type { Lang } from "@/lib/types";

const LOCALE_NAMES = languageNames;
const LOCALE_FLAG_SRC = localeRegions.flagAssets as Partial<Record<Lang, string>>;

function persistLanguage(locale: Lang) {
  persistLanguageChoice(locale);
}

function useSwitcherLanguages(pathname: string) {
  const siteLocale = useSiteLocale();
  const { navigation } = useBlogLocaleNavigation();
  const blog = navigation?.pathname === pathname.replace(/\/$/, "") ? navigation : null;
  const currentLocale = blog?.lang ?? siteLocale;
  const options = getLanguageSwitchOptions(pathname, blog);
  return {
    currentLocale,
    label: localizeCopy(blogCopy.languageLabel, currentLocale),
    locales: options.map((option) => option.locale),
    href: (locale: Lang) => options.find((option) => option.locale === locale)!.href,
  };
}

function CcLocaleFlag({
  locale,
  decorative = false,
  className,
}: {
  locale: Lang;
  /** When true, adjacent text carries the accessible name (popover rows). */
  decorative?: boolean;
  className?: string;
}) {
  const name = LOCALE_NAMES[locale];

  if (!LOCALE_FLAG_SRC[locale]) return <span className={className} aria-hidden="true">{languageFlag(locale)}</span>;
  return (
    <img
      src={LOCALE_FLAG_SRC[locale]}
      alt={decorative ? "" : name}
      width={18}
      height={12}
      className={["cc-lang-switcher__flag", className].filter(Boolean).join(" ")}
      loading="lazy"
      decoding="async"
    />
  );
}

type CcLangSwitcherProps = {
  className?: string;
  /** Public locale pill links. Footer and mobile nav drawer use `popover`. */
  variant?: "inline" | "popover";
};

export function CcLangSwitcher({
  className,
  variant = "inline",
}: CcLangSwitcherProps) {
  if (variant === "popover") {
    return <CcLangSwitcherPopover className={className} />;
  }

  return <CcLangSwitcherInline className={className} />;
}

function CcLangSwitcherInline({ className }: { className?: string }) {
  const pathname = usePathname() ?? "/";
  const { currentLocale, locales, label, href: localeHref } = useSwitcherLanguages(pathname);

  return (
    <nav
      className={["cc-lang-switcher", className].filter(Boolean).join(" ")}
      aria-label={label}
    >
      <ul className="cc-lang-switcher__list">
        {locales.map((locale) => {
          const isActive = locale === currentLocale;
          const href = localeHref(locale);

          return (
            <li key={locale}>
              <a
                href={href}
                className={`cc-lang-switcher__link${isActive ? " cc-lang-switcher__link--active" : ""}`}
                aria-current={isActive ? "true" : undefined}
                lang={locale}
                onClick={() => persistLanguage(locale)}
              >
                {locale.toUpperCase()}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function CcLangSwitcherPopover({ className }: { className?: string }) {
  const pathname = usePathname() ?? "/";
  const { currentLocale, locales, label, href: localeHref } = useSwitcherLanguages(pathname);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Partial<Record<Lang, HTMLAnchorElement>>>({});
  const listboxId = useId();
  const labelId = useId();

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const root = rootRef.current;
      if (root && !root.contains(event.target as Node)) {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open, close]);

  const focusOption = (locale: Lang) => {
    optionRefs.current[locale]?.focus();
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
      }
      const target =
        event.key === "ArrowDown"
          ? locales.find((locale) => locale !== currentLocale) ?? locales[0]
          : [...locales].reverse().find((locale) => locale !== currentLocale) ??
            locales[locales.length - 1];
      requestAnimationFrame(() => focusOption(target));
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((wasOpen) => !wasOpen);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  const handleListboxKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const focusedIndex = locales.findIndex((locale) => optionRefs.current[locale] === event.target);
    const index = focusedIndex >= 0 ? focusedIndex : locales.findIndex((locale) => locale === currentLocale);

    if (event.key === "Escape") {
      event.preventDefault();
      close();
      triggerRef.current?.focus();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = locales[(index + 1) % locales.length];
      focusOption(next);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      const prev =
        locales[(index - 1 + locales.length) % locales.length];
      focusOption(prev);
    }
  };

  return (
    <div
      ref={rootRef}
      className={["cc-lang-switcher", "cc-lang-switcher--popover", className]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        ref={triggerRef}
        type="button"
        id={labelId}
        className="cc-lang-switcher__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={`${label}: ${LOCALE_NAMES[currentLocale]}`}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={handleTriggerKeyDown}
      >
        <CcLocaleFlag locale={currentLocale} className="cc-lang-switcher__trigger-flag" />
        <span className="cc-lang-switcher__trigger-label">
          {LOCALE_NAMES[currentLocale]}
        </span>
        <ChevronDown
          className="cc-lang-switcher__chevron"
          data-open={open ? "true" : "false"}
          aria-hidden="true"
          size={14}
          strokeWidth={2}
        />
      </button>

      {open ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-labelledby={labelId}
          className="cc-lang-switcher__panel"
          onKeyDown={handleListboxKeyDown}
        >
          {locales.map((locale) => {
            const isActive = locale === currentLocale;
            const href = localeHref(locale);

            return (
              <li key={locale} role="presentation">
                <a
                  ref={(node) => {
                    if (node) {
                      optionRefs.current[locale] = node;
                    }
                  }}
                  href={href}
                  role="option"
                  aria-selected={isActive}
                  className={`cc-lang-switcher__option${isActive ? " cc-lang-switcher__option--active" : ""}`}
                  lang={locale}
                  onClick={() => {
                    persistLanguage(locale);
                    close();
                  }}
                >
                  <CcLocaleFlag locale={locale} decorative />
                  <span className="cc-lang-switcher__option-name">
                    {LOCALE_NAMES[locale]}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
